import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getTableCellErrorState } from '@/components/ui-v2/inputs/table/hooks/get-table-cell-error-state';
import { TablePriceDisplay } from '@/components/ui-v2/inputs/table/table-price-display';
import { TablePriceInput } from '@/components/ui-v2/inputs/table/table-price-input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  DealItem,
  DealItemWithVersions,
  LegalStatus,
  PricingMode,
} from '@/lib/graphql/generated/graphql';
import { formatCurrency } from '@/lib/utils';
import { useSetColumnSort } from '@/states/column-sort.state';
import { pricingModeState } from '@/states/pricing-mode.state';
import { storeAtom } from '@/states/store.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue } from 'jotai';
import { InfoIcon } from 'lucide-react';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';
import { TableCellWithRecentlyModified } from './table-cell-with-recently-modified';

interface ColumnProps {
  handleCellClick: (id: string, field: keyof DealItem) => void;
  handleCellEdit: <K extends keyof DealItem>(
    id: string | undefined,
    field: K,
    value: DealItem[K],
    isLeafRow?: boolean
  ) => Promise<void>;
  handleKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  handleBlur: React.FocusEventHandler<HTMLInputElement>;
}

interface BuyingPriceCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  handleCellEdit: ColumnProps['handleCellEdit'];
  handleKeyDown: ColumnProps['handleKeyDown'];
  handleBlur: ColumnProps['handleBlur'];
}

function BuyingPriceCellContent({
  row,
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: BuyingPriceCellContentProps) {
  const store = useAtomValue(storeAtom);
  const isOfferLocked = useIsOfferLocked();
  const pricingMode = useAtomValue(pricingModeState);
  const isEditing = useIsCellEditing(row, 'buyingPrice');
  const isParent = row.subRows.length > 1;

  const isBuyingPriceEditable =
    pricingMode === PricingMode.BuyingPriceFixed && !isOfferLocked;

  const getValue = () => {
    const value = row.original.activeVersion.buyingPrice?.toFixed(2);
    if (typeof value === 'undefined' || value === null) return '—';
    return formatCurrency(parseFloat(value), store);
  };

  const getParentValue = () => {
    const subRowsValues: number[] = [];
    row.subRows.forEach((subRow) => {
      const value = subRow.original.activeVersion.buyingPrice?.toFixed(2);
      if (typeof value === 'undefined' || value === null) return;
      subRowsValues.push(parseFloat(value));
    });
    if (subRowsValues.length === 0) return '—';
    const min = formatCurrency(Math.min(...subRowsValues), store);
    const max = formatCurrency(Math.max(...subRowsValues), store);
    return min === max ? max : `${min} - ${max}`;
  };

  const errorState = getTableCellErrorState({
    row,
    field: 'buyingPrice',
    errorType: 'empty',
  });

  return (
    <TableCellWithRecentlyModified
      productId={row.original.id}
      field="buyingPrice"
      isParent={isParent}
    >
      {(isRecentlyModified) => (
        <div className="h-full w-full">
          {isEditing ? (
            <TablePriceInput
              value={row.original.activeVersion.buyingPrice ?? null}
              onChange={async (value) => {
                await handleCellEdit(
                  row.original.activeVersion.id,
                  'buyingPrice',
                  value,
                  !isParent
                );
              }}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              disabled={!isBuyingPriceEditable}
              autoFocus={isEditing}
              hasError={errorState.hasError}
            />
          ) : (
            <TablePriceDisplay
              value={
                errorState.isEmpty
                  ? null
                  : isParent
                    ? getParentValue()
                    : getValue()
              }
              isEmpty={errorState.isEmpty}
              hasError={errorState.hasError}
              errorMessage={errorState.errorMessage}
              isEditable={isBuyingPriceEditable}
              isParent={isParent}
              isRecentlyModified={isRecentlyModified}
              onClick={() =>
                isBuyingPriceEditable &&
                !isParent &&
                handleCellClick(row.original.activeVersion.id, 'buyingPrice')
              }
            />
          )}
        </div>
      )}
    </TableCellWithRecentlyModified>
  );
}

const useColumnDefBuyingPrice = ({
  handleCellClick,
  handleCellEdit,
  handleKeyDown,
  handleBlur,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();
  const { deal } = useDealContext();
  const isAutoEntrepreneurOffer =
    deal?.legalStatus === LegalStatus.AutoEntrepreneur;

  const accessorKey: keyof DealItem = 'buyingPrice';

  return useMemo(
    () => ({
      accessorKey: accessorKey,
      name: 'buyingPrice',
      accessorFn: (row) => row.activeVersion?.buyingPrice ?? 0,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div className="cursor-pointer font-semibold">
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <InfoIcon className="h-4 w-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {t(
                    isAutoEntrepreneurOffer
                      ? 'kpi.buying_price_tooltip_self_employed'
                      : 'kpi.buying_price_tooltip'
                  )}
                </p>
              </TooltipContent>
            </Tooltip>{' '}
            <div
              className="flex flex-1 items-center gap-2"
              onClick={() => setColumnSort(accessorKey)}
            >
              {t('kpi.buying_price')}
              {!isAutoEntrepreneurOffer && <> ({t('kpi.excl_vat')})</>}
              <SortIcon pointer="buyingPrice" />
            </div>
          </div>
        </div>
      ),
      cell: ({ row }) => (
        <BuyingPriceCellContent
          row={row}
          handleCellClick={handleCellClick}
          handleCellEdit={handleCellEdit}
          handleKeyDown={handleKeyDown}
          handleBlur={handleBlur}
        />
      ),
      size: 180,
    }),
    [
      t,
      setColumnSort,
      isAutoEntrepreneurOffer,
      handleCellClick,
      handleCellEdit,
      handleKeyDown,
      handleBlur,
    ]
  );
};

export default useColumnDefBuyingPrice;
